import { CreateAreaRequest } from "@/services/backend/qpm/buildings/types/building";
import { AreaType } from "@/services/backend/qpm/buildings/types/building-enum";
import { useToast } from "@/shared/hooks/use-toast";
import { useCallback, useEffect, useState } from "react";
import { useQpmAreaAddMutation } from "@/services/backend/qpm/buildings/api/building-api";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { v4 } from "uuid";
import { Loader2, Plus } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";

export type NewAreaPopOverButtonProps = {
  areaType: AreaType;
  label: string;
  buildingId: string;
  levelId?: string;
  description?: string;
};

export function NewAreaPopOverButton({
  areaType,
  label,
  buildingId,
  levelId,
  description,
}: NewAreaPopOverButtonProps) {
  const { toast } = useToast();
  const [areaName, setAreaName] = useState<string>("");
  const [createNewArea, { error, reset, isSuccess, isLoading }] =
    useQpmAreaAddMutation();

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error) });
      reset();
    }
  }, [error, reset, toast]);

  useEffect(() => {
    if (isSuccess) {
      setAreaName("");
    }
  }, [isSuccess]);

  const handleSave = useCallback(() => {
    if (isLoading || !areaName) {
      return;
    }
    const request: CreateAreaRequest = {
      id: v4(),
      buildingId,
      levelId,
      name: areaName.trim(),
      type: areaType,
    };
    createNewArea(request);
  }, [areaName, areaType, buildingId, createNewArea, isLoading, levelId]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus />}
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="leading-none font-medium">{label}</h4>
            <p className="text-muted-foreground text-sm">
              {description ??
                t("Geben Sie hier den Namen des neuen Bereichs ein.")}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`new-area-input-${areaType}-${buildingId}`}>
              {t("Bereichsname")}
            </Label>
            <Input
              id={`new-area-input-${areaType}-${buildingId}`}
              value={areaName}
              onChange={(event) => setAreaName(event.target.value)}
              disabled={isLoading}
              onKeyDown={(event) => {
                if (event.key === "Enter" && areaName && !isLoading) {
                  handleSave();
                }
              }}
            />
          </div>
          <Button disabled={isLoading || !areaName.trim()} onClick={handleSave}>
            {t("Speichern")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
